import { copy } from '@shared/copy';
import { cn } from '@shared/ui/cn';
import { DangerIcon, InfoIcon } from '@shared/ui/icons';

/**
 * The inline failure banner — three parts: what happened, what was unaffected,
 * and what to do now.
 *
 * **A refusal on the caller's role is a fourth thing, and it renders as one.**
 * `kind="permission"` retitles the banner, drops the unaffected line, and
 * withholds the retry control. Rendering it as a failure was wrong three times
 * in one card: «تعذّر إتمام الطلب» describes an attempt that failed, «لم يتغيّر
 * شيء في الفهرس» answers a question nobody asked on a read, and «أعد المحاولة»
 * offers a button that cannot succeed however many times it is pressed. A role
 * is granted from the console, so what replaces the button is a sentence saying
 * where it comes from.
 *
 * **Tone is not decoration.** A `502` or a `504` is neutral: the failure is
 * upstream and nobody in this console caused it, and a danger-styled banner
 * teaches an operator that the platform is angry at them. Danger is reserved for
 * a destructive action that failed.
 *
 * NO CORRELATION ID IS DRAWN. The `X-Request-ID` is read off the response and
 * goes to the log line and to telemetry; the banner names what happened and
 * what to do, and an operator quotes that rather than a hex string. No status
 * numeral and no `error_code` reaches the interface either.
 */
export function ErrorBanner({
  message,
  unaffected = copy.error.unaffected,
  tone = 'neutral',
  kind = 'failure',
  onRetry,
}: {
  message: string;
  unaffected?: string;
  tone?: 'neutral' | 'danger';
  /** `permission` for a refusal on the caller's role; everything else is a failure. */
  kind?: 'failure' | 'permission';
  onRetry?: () => void;
}) {
  const permission = kind === 'permission';
  const danger = tone === 'danger' && !permission;
  const Glyph = danger ? DangerIcon : InfoIcon;
  // Withheld rather than left to the call site: a retry that cannot succeed is a
  // property of the refusal, not a decision each of thirteen call sites should
  // be trusted to repeat.
  const retry = permission ? undefined : onRetry;

  return (
    <div
      role="alert"
      className={cn(
        'flex items-start gap-3 rounded-md border border-s-4 border-border p-4 text-sm',
        danger ? 'border-s-danger bg-danger-soft' : 'border-s-info bg-info-soft',
      )}
    >
      <Glyph className={cn('mt-1 shrink-0', danger ? 'text-danger' : 'text-info')} />

      <div className="min-w-0">
        <p className="mb-1 max-w-none font-semibold text-ink">
          {permission ? copy.error.permissionTitle : copy.error.title}
        </p>
        <p className="mb-0 max-w-none text-ink-muted">{message}</p>
        <p className="mb-0 mt-1 max-w-none text-ink-muted">
          {permission ? copy.error.permissionNextStep : unaffected}
        </p>

        <div className="mt-3 flex flex-wrap items-center gap-3">
          {retry ? (
            <button
              type="button"
              onClick={retry}
              className="hit-target rounded-sm border border-border-strong bg-surface-raised px-3 text-sm font-semibold text-ink hover:bg-surface-overlay"
            >
              {copy.action.retry}
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
}
